import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { playMessagePing } from "@/lib/notifySound";

export const THREAD_POLL_MS = 8000;
const NEAR_BOTTOM_PX = 80;

// Lot 777 — titre d'onglet « (N) Nouveau message · … » tant que les messages arrivés ne sont pas vus ; restauré ensuite.
const tabTitle = { base: null, n: 0 };
export const setThreadTitle = (n, label) => {
  if (typeof document === "undefined") return;
  if (n > 0) {
    if (tabTitle.base === null) tabTitle.base = document.title;
    document.title = `(${n}) ${label} · ${tabTitle.base}`;
  } else if (tabTitle.base !== null) {
    document.title = tabTitle.base;
    tabTitle.base = null;
  }
  tabTitle.n = n;
};

// Lot 776 — le fil d'un ticket se comporte comme un chat : ouverture sur le premier message non lu (sinon le dernier),
// mes envois ramènent en bas, les messages de l'autre partie arrivent seuls (toutes les 8 s) ; si je lis plus haut,
// une pastille « N nouveaux messages » propose de descendre. Lot 777 : son discret + titre d'onglet à l'arrivée.
// Lot 780 — garde-fous : `chat` faux → comportement d'avant 776 (ouverture en haut, pas de rafraîchissement, pas de repère) ;
// `alerts` faux → ni son ni titre d'onglet.
export const useTicketThread = ({ ticketId, meId, onTicket, titleLabel, chat = true, alerts = true }) => {
  const [messages, setMessages] = useState([]);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [pendingBelow, setPendingBelow] = useState(0);
  const boxRef = useRef(null);
  const lastAt = useRef(null);
  const ticketRef = useRef(onTicket);
  ticketRef.current = onTicket;
  const labelRef = useRef(titleLabel);
  labelRef.current = titleLabel;
  const alertsRef = useRef(alerts);
  alertsRef.current = alerts;
  const title = (n) => { if (n > 0 && !alertsRef.current) return; setThreadTitle(n, (typeof labelRef.current === "function" ? labelRef.current(n) : labelRef.current) || (n > 1 ? "Nouveaux messages" : "Nouveau message")); };

  const nearBottom = () => { const el = boxRef.current; return !el || el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX; };
  const scrollToBottom = useCallback((smooth = true) => {
    const el = boxRef.current;
    programmatic.current = Date.now();
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    setPendingBelow(0);
    title(0);
  }, []);  
  const scrollToMessage = (id) => {
    const el = boxRef.current;
    const node = el?.querySelector(`[data-mid="${id}"]`);
    if (!el || !node) return false;
    el.scrollTop = node.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 12;
    return true;
  };
  const track = (msgs) => { lastAt.current = msgs.length ? msgs[msgs.length - 1].created_at : lastAt.current; };
  // Repère d'ouverture ré-appliqué quelques instants (polices, photos qui changent les hauteurs) tant que je n'ai pas scrollé.
  const anchor = useRef(null);
  const programmatic = useRef(0);
  const place = () => {
    const a = anchor.current;
    if (!a) return;
    programmatic.current = Date.now();
    if (a === "bottom") { const el = boxRef.current; if (el) el.scrollTop = el.scrollHeight; } else if (!scrollToMessage(a)) { const el = boxRef.current; if (el) el.scrollTop = el.scrollHeight; }
  };
  const settle = (target) => {
    anchor.current = target;
    requestAnimationFrame(place);
    [250, 900].forEach((ms) => setTimeout(place, ms));
    setTimeout(() => { anchor.current = null; }, 1200);
  };

  // Ouverture : repère sur le premier message des autres postérieur à ma dernière lecture, sinon en bas.
  const open = useCallback((data) => {
    const msgs = data?.messages || [];
    setMessages(msgs);
    track(msgs);
    if (!chat) { setFirstUnreadId(null); setPendingBelow(0); return; }   // avant 776 : liste telle quelle, en haut
    const lr = data?.last_read_at || "";
    const first = msgs.find((m) => m.sender_id !== meId && (!lr || m.created_at > lr));
    setFirstUnreadId(first?.id || null);
    setPendingBelow(0);
    settle(first ? first.id : "bottom");
  }, [meId, chat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rafraîchissement après une action (prise en charge, résolu…) : messages à jour, sans bouger la lecture.
  const sync = useCallback((data) => { const msgs = data?.messages || []; setMessages(msgs); track(msgs); }, []);

  // Mon envoi : ajouté au fil, descente en bas.
  const append = useCallback((m) => {
    setMessages((ms) => (ms.some((x) => x.id === m.id) ? ms : [...ms, m]));
    track([m]);
    if (chat) requestAnimationFrame(() => scrollToBottom(true));
  }, [scrollToBottom, chat]);

  const onScroll = () => {
    if (Date.now() - programmatic.current > 150) anchor.current = null;   // le lecteur a bougé : on ne le replace plus
    if (pendingBelow && nearBottom()) { setPendingBelow(0); if (!document.hidden) title(0); }
  };

  useEffect(() => {
    if (!ticketId || !chat) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/support/tickets/${ticketId}/messages`, { params: { after: lastAt.current || "" } });
        if (!alive) return;
        const fresh = data.messages || [];
        if (fresh.length) {
          const wasNear = nearBottom();
          setMessages((ms) => { const ids = new Set(ms.map((x) => x.id)); return [...ms, ...fresh.filter((m) => !ids.has(m.id))]; });
          track(fresh);
          const others = fresh.filter((m) => m.sender_id !== meId).length;
          if (others && alertsRef.current) {
            playMessagePing();   // son discret (réglage « son des messages » respecté)
            // Titre d'onglet tant que ce n'est pas vu : onglet caché, ou lecture plus haut dans le fil.
            if (document.hidden || !wasNear) title(tabTitle.n + others);
          }
          if (wasNear && !document.hidden) requestAnimationFrame(() => scrollToBottom(true));
          else if (wasNear) requestAnimationFrame(() => { const el = boxRef.current; if (el) { programmatic.current = Date.now(); el.scrollTop = el.scrollHeight; } });
          else if (others) setPendingBelow((n) => n + others);
        }
        if (data.ticket) ticketRef.current?.(data.ticket);
      } catch { /* réseau : on réessaie au prochain tour */ }
    };
    const id = setInterval(tick, THREAD_POLL_MS);
    // De retour sur l'onglet et déjà en bas → vu : le titre redevient normal.
    const onVisible = () => { if (!document.hidden && nearBottom()) title(0); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); title(0); };
  }, [ticketId, meId, scrollToBottom, chat]);  

  return { messages, setMessages, firstUnreadId, pendingBelow, boxRef, open, sync, append, scrollToBottom, onScroll };
};
